import React, { useState, useRef, useEffect } from "react";

const BiCalendar = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const BiX = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const DualDatePicker = ({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  format = "date",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showYearPicker, setShowYearPicker] = useState(false);
  const datePickerRef = useRef(null);

  useEffect(() => {
    if (value) {
      setCurrentMonth(new Date(value));
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setShowYearPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);

    if (format === "month") {
      return date.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const clearDate = (e) => {
    e.stopPropagation();
    onChange("");
    setIsOpen(false);
  };

  const prevMonth = () => {
    setCurrentMonth((prevState) => {
      const prevMonthDate = new Date(prevState);
      prevMonthDate.setMonth(prevMonthDate.getMonth() - 1);
      return prevMonthDate;
    });
  };

  const nextMonth = () => {
    setCurrentMonth((prevState) => {
      const nextMonthDate = new Date(prevState);
      nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);
      return nextMonthDate;
    });
  };

  const handleYearSelect = (year) => {
    setCurrentMonth((prevState) => {
      const newDate = new Date(prevState);
      newDate.setFullYear(year);
      return newDate;
    });
    setShowYearPicker(false);
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const handleDateClick = (day) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const newDate = new Date(Date.UTC(year, month, day))
      .toISOString()
      .split("T")[0];

    onChange(newDate);
    setIsOpen(false);
  };

  const handleMonthClick = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const newDate = new Date(Date.UTC(year, month, 1))
      .toISOString()
      .split("T")[0];

    onChange(newDate);
    setIsOpen(false);
  };

  const isDateSelected = (day) => {
    if (!value) return false;
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    )
      .toISOString()
      .split("T")[0];
    return date === value;
  };

  const isDateDisabled = (day) => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    )
      .toISOString()
      .split("T")[0];

    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const renderYearPicker = () => {
    const currentYear = currentMonth.getFullYear();
    const years = [];

    for (let i = currentYear - 50; i <= currentYear + 10; i++) {
      years.push(i);
    }

    return (
      <div className="bg-white rounded-lg shadow-lg p-4 max-h-64 overflow-y-auto">
        <div className="grid grid-cols-2 gap-2">
          {years.map((year) => (
            <button
              key={year}
              onClick={() => handleYearSelect(year)}
              className={`
                p-2 rounded-md text-sm font-medium transition-all
                ${
                  year === currentYear
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }
              `}
            >
              {year}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const monthName = currentMonth.toLocaleDateString("en-US", {
      month: "long",
    });

    if (showYearPicker) {
      return renderYearPicker();
    }

    if (format === "month") {
      return (
        <div className="bg-white min-w-60 rounded-lg shadow-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <button
              className="p-1 rounded-full hover:bg-gray-100"
              onClick={prevMonth}
              aria-label="Previous month"
            >
              <svg
                className="h-6 w-6 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={() => setShowYearPicker(true)}
              className="text-sm font-bold hover:bg-gray-100 px-3 py-1 rounded-md transition-colors"
            >
              {monthName} <span className="text-gray-700">{year}</span>
            </button>

            <button
              className="p-1 rounded-full hover:bg-gray-100"
              onClick={nextMonth}
              aria-label="Next month"
            >
              <svg
                className="h-6 w-6 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          <div className="border-t pt-4 flex justify-between items-center">
            <button
              onClick={clearDate}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 
                transition-colors rounded-md hover:bg-gray-100"
            >
              Clear
            </button>
            <button
              onClick={handleMonthClick}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 
                hover:bg-blue-700 transition-colors rounded-md"
            >
              Select Month
            </button>
          </div>
        </div>
      );
    }

    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);
    const daysBefore = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

    const days = [];
    const prevMonthDays = getDaysInMonth(year, month - 1);
    for (let i = prevMonthDays - daysBefore + 1; i <= prevMonthDays; i++) {
      days.push({ day: i, currentMonth: false });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, currentMonth: true });
    }

    const totalDaysShown = Math.ceil((daysInMonth + daysBefore) / 7) * 7;
    const daysAfter = totalDaysShown - (daysInMonth + daysBefore);
    for (let i = 1; i <= daysAfter; i++) {
      days.push({ day: i, currentMonth: false });
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <div className="bg-white min-w-60 rounded-lg shadow-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <button
            className="p-1 rounded-full hover:bg-gray-100"
            onClick={prevMonth}
            aria-label="Previous month"
          >
            <svg
              className="h-6 w-6 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={() => setShowYearPicker(true)}
            className="text-sm font-bold hover:bg-gray-100 px-3 py-1 rounded-md transition-colors"
          >
            {monthName} <span className="text-gray-700">{year}</span>
          </button>

          <button
            className="p-1 rounded-full hover:bg-gray-100"
            onClick={nextMonth}
            aria-label="Next month"
          >
            <svg
              className="h-6 w-6 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2 text-black">
          <div className="text-center text-sm font-medium">Mo</div>
          <div className="text-center text-sm font-medium">Tu</div>
          <div className="text-center text-sm font-medium">We</div>
          <div className="text-center text-sm font-medium">Th</div>
          <div className="text-center text-sm font-medium">Fr</div>
          <div className="text-center text-sm font-medium">Sa</div>
          <div className="text-center text-sm font-medium">Su</div>
        </div>

        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 gap-1 mb-1">
            {week.map((day, dayIndex) => {
              const isSelected = day.currentMonth && isDateSelected(day.day);
              const isDisabled = day.currentMonth && isDateDisabled(day.day);

              return (
                <button
                  key={dayIndex}
                  onClick={() =>
                    day.currentMonth && !isDisabled && handleDateClick(day.day)
                  }
                  disabled={isDisabled}
                  className={`
                    p-1 flex items-center justify-center rounded-md text-xs shadow shadow-gray-300
                    ${!day.currentMonth ? "text-gray-300" : "text-gray-700"}
                    ${isSelected ? "bg-blue-600 text-white" : ""}
                    ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}
                    ${
                      day.currentMonth && !isSelected && !isDisabled
                        ? "hover:bg-gray-100"
                        : ""
                    }
                  `}
                >
                  {day.day}
                </button>
              );
            })}
          </div>
        ))}

        <div className="border-t pt-2 flex justify-between items-center">
          <button
            onClick={clearDate}
            className="px-4 text-sm font-medium text-gray-700 hover:text-gray-900 
              transition-colors rounded-md hover:bg-gray-100"
          >
            Clear
          </button>
          <div className="text-sm text-gray-600">
            {value && <span>{formatDate(value)}</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative w-full" ref={datePickerRef}>
      <div
        className={`w-full bg-white rounded-lg shadow-sm border 
        transition-all duration-200 ease-in-out cursor-pointer p-1
        ${
          isOpen
            ? "ring-2 ring-blue-500 border-blue-500"
            : "border-gray-300 hover:border-gray-400"
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center space-x-3 p-2">
          <div className="text-gray-600">
            <BiCalendar />
          </div>

          <div className="flex flex-col flex-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {value ? (
                  <>
                    <span className="text-sm font-medium text-gray-900">
                      {formatDate(value)}
                    </span>
                    <button
                      onClick={clearDate}
                      className="p-1 hover:bg-gray-100 rounded-full transition-all duration-200"
                      aria-label="Clear date"
                    >
                      <BiX />
                    </button>
                  </>
                ) : (
                  <span className="text-sm font-medium text-gray-600">
                    {label}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isOpen && (
        <div
          className="absolute top-full mt-1 w-full bg-white rounded-lg shadow-lg 
          border border-gray-200 z-50 transform transition-all duration-200 ease-out md:right-24 right-0"
        >
          {renderCalendar()}
        </div>
      )}
    </div>
  );
};

export default DualDatePicker;
